import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { UserService } from '../../services/userService';
import { User } from '../../types/auth';

interface UserBadgeProps {
  personId: string;
}

/**
 * Shows if a person has a linked user account
 * Displays badge and link to user profile
 */
const UserBadge: React.FC<UserBadgeProps> = ({ personId }) => {
  const navigate = useNavigate();
  const [linkedUsers, setLinkedUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLinkedUsers = async () => {
      try {
        const users = await UserService.getUsersLinkedToPerson(personId);
        setLinkedUsers(users);
      } catch (err) {
        console.error('Error loading linked users:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLinkedUsers();
  }, [personId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (linkedUsers.length === 0) {
    return null;
  }

  const linkedUser = linkedUsers[0]; // Show first linked user (should only be one)

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Chip
        icon={<PersonIcon />}
        label="Platform Member"
        color="primary"
        size="small"
      />
      <Button
        size="small"
        variant="outlined"
        onClick={() => navigate(`/user/${linkedUser.uid}`)}
      >
        View User Profile
      </Button>
    </Stack>
  );
};

export default UserBadge;
